/**
 * remarkCitations — remark plugin for citation markers.
 *
 * Handles two sentinel patterns emitted by Athena:
 *   ‹cite:N›            — inline citation → <sup data-citation-id="N">
 *   ‹table-sources:1,5› — after a table   → table node data-table-sources="1,5"
 *
 * CRITICAL (react-markdown v10): react-markdown resolves `components` by the
 * hast element tagName, NOT by a custom mdast node type. A custom mdast type is
 * silently dropped during mdast→hast. We therefore emit a node with
 * `data.hName = 'sup'` + `data.hProperties = { 'data-citation-id': N }` so the
 * hast layer produces a real <sup data-citation-id="N">; `components.sup` in
 * MessagesContainer.tsx branches on that attr to render CitationMarker.
 *
 * Table footers: a `‹table-sources:…›` token is attached to the NEAREST PRECEDING
 * table in document order (not just the immediate sibling — prose often sits
 * between the table and the sentinel), via the table node's
 * `data.hProperties['data-table-sources']`. The sentinel text is stripped, and a
 * paragraph that becomes empty as a result is removed. In-table ‹cite:N› are
 * stripped from cell text and folded into the enclosing table's accumulator.
 *
 * Implementation: a single depth-first, document-order walk that tracks the last
 * table node seen. This is robust to intervening prose, multiple tables, and
 * sentinels nested inside lists/blockquotes — the previous root-only,
 * immediate-sibling scan missed all of those (table-footer leak fixed by switching
 * to a full DFS walk).
 */

import { UnistNode } from './MessagesContainer.types';
import { CITATION_SENTINEL_REGEX, TABLE_SOURCES_SENTINEL_REGEX } from './MessagesContainer.constants';

// Fresh global regexes per use — RegExp.exec with /g is stateful, so never share.
const citeRe = () => new RegExp(CITATION_SENTINEL_REGEX.source, 'g');
const tableSourcesRe = () => new RegExp(TABLE_SOURCES_SENTINEL_REGEX.source, 'g');

const makeCitationNode = (id: number): UnistNode => ({
    type: 'citationMarker',
    data: { hName: 'sup', hProperties: { 'data-citation-id': id } },
    children: [{ type: 'text', value: String(id) }],
});

/** Merge a set of source ids into a table node's data-table-sources property. */
const addTableSources = (tableNode: UnistNode | null, ids: number[]): void => {
    if (!tableNode || ids.length === 0) return;
    tableNode.data = tableNode.data ?? {};
    tableNode.data.hProperties = tableNode.data.hProperties ?? {};
    const existing = tableNode.data.hProperties['data-table-sources'];
    const existingIds = existing ? String(existing).split(',').map(Number) : [];
    const merged = [...new Set([...existingIds, ...ids])].filter((n) => !Number.isNaN(n));
    tableNode.data.hProperties['data-table-sources'] = merged.join(',');
};

const parseIds = (csv: string): number[] =>
    csv
        .split(',')
        .map((n) => parseInt(n.trim(), 10))
        .filter((n) => !Number.isNaN(n));

/** Split a text value on ‹cite:N› (or ‹cite:N,M›), interleaving text + one marker per id. */
const splitTextOnCitations = (value: string): UnistNode[] | null => {
    const parts: UnistNode[] = [];
    let lastIndex = 0;
    const re = citeRe();
    let match: RegExpExecArray | null;

    while ((match = re.exec(value)) !== null) {
        if (match.index > lastIndex) {
            parts.push({ type: 'text', value: value.slice(lastIndex, match.index) });
        }
        // A single marker may bundle several ids (‹cite:3,4›) — emit one sup per id.
        for (const id of parseIds(match[1])) parts.push(makeCitationNode(id));
        lastIndex = match.index + match[0].length;
    }

    if (parts.length === 0) return null;
    if (lastIndex < value.length) {
        parts.push({ type: 'text', value: value.slice(lastIndex) });
    }
    return parts;
};

/** True if a node carries no rendered content (used to prune emptied paragraphs). */
const isEmptyNode = (node: UnistNode): boolean => {
    if (node.type === 'text') return (node.value ?? '') === '';
    if (node.children) return node.children.every(isEmptyNode);
    // a citationMarker / element with no children still renders — not empty
    return false;
};

interface WalkCtx {
    lastTable: UnistNode | null;
}

/**
 * Handle a text node that lives inside a table cell: fold any ‹cite:N› ids into
 * the table footer accumulator and strip the sentinels from the cell text.
 * Returns the cleaned text (may be empty string — caller decides whether to keep it).
 */
const handleTextInsideTable = (text: string, tableNode: UnistNode): string => {
    const ids: number[] = [];
    const re = citeRe();
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) ids.push(...parseIds(match[1]));
    if (ids.length) addTableSources(tableNode, ids);
    return ids.length ? text.replace(citeRe(), '') : text;
};

/**
 * Handle a text node outside any table: split on ‹cite:N› to produce inline
 * citation marker nodes. Returns replacement nodes (text + markers interleaved),
 * or null if no citations were found (caller keeps the original node unchanged).
 */
const handleTextOutsideTable = (text: string): UnistNode[] | null => splitTextOnCitations(text);

/**
 * Depth-first, document-order walk. Tracks the last table seen so a
 * ‹table-sources:…› token anywhere after a table attaches to it. Inside a table,
 * ‹cite:N› are folded into the table footer instead of rendered inline.
 */
const walk = (node: UnistNode, ctx: WalkCtx, insideTable: UnistNode | null): void => {
    if (!node.children) return;

    const out: UnistNode[] = [];

    for (const child of node.children) {
        if (child.type === 'table') {
            ctx.lastTable = child;
            walk(child, ctx, child); // process cells with this table as the footer target
            out.push(child);
            continue;
        }

        if (child.type === 'text' && child.value !== undefined) {
            let text = child.value;

            // 1) Pull out ‹table-sources:…› → attach to the relevant table, strip from text.
            const tsRe = tableSourcesRe();
            let tsMatch: RegExpExecArray | null;
            let foundTableSources = false;
            while ((tsMatch = tsRe.exec(text)) !== null) {
                // In-table sentinel attaches to its own table; otherwise to the last table seen.
                addTableSources(insideTable ?? ctx.lastTable, parseIds(tsMatch[1]));
                foundTableSources = true;
            }
            if (foundTableSources) text = text.replace(tableSourcesRe(), '');

            if (insideTable) {
                const cleaned = handleTextInsideTable(text, insideTable);
                if (cleaned !== '') out.push({ type: 'text', value: cleaned });
            } else {
                const replaced = handleTextOutsideTable(text);
                if (replaced) {
                    out.push(...replaced);
                } else if (text !== '') {
                    out.push({ type: 'text', value: text });
                }
                // if text is now '' (was a pure table-sources line) → drop it
            }
            continue;
        }

        // Other container node: recurse, then drop it if it ended up empty
        // (e.g. a paragraph that held only a ‹table-sources:…› line).
        walk(child, ctx, insideTable);
        if (child.type === 'paragraph' && (!child.children || child.children.length === 0 || isEmptyNode(child))) {
            continue; // prune emptied paragraph
        }
        out.push(child);
    }

    node.children = out;
};

/** The remark plugin. Usage: remarkPlugins={[remarkGfm, remarkCitations]} */
const remarkCitations = () => (tree: UnistNode) => {
    walk(tree, { lastTable: null }, null);
};

export default remarkCitations;
